import React, { useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { CalendarDays } from "lucide-react";

// English-locale date picker. Native <input type="date"> follows the BROWSER
// locale (a Russian-locale browser shows a Russian placeholder on an English
// site) — this component renders the same English calendar for every visitor.
// value/onChange use the YYYY-MM-DD string format (same as native inputs).

const CLASSES = {
  root: "wtd-datepicker",
  months: "flex",
  month: "space-y-3",
  caption: "flex justify-center pt-1 relative items-center",
  caption_label: "rdp-caption_label text-sm font-medium text-white",
  nav: "space-x-1 flex items-center",
  nav_button: "rdp-nav_button h-7 w-7 bg-transparent p-0 text-white/50 hover:text-white border border-white/10 rounded-md inline-flex items-center justify-center transition-colors",
  nav_button_previous: "absolute left-1",
  nav_button_next: "absolute right-1",
  table: "w-full border-collapse",
  head_row: "flex",
  head_cell: "text-white/35 rounded-md w-9 font-normal text-[11px] text-center",
  row: "flex w-full mt-1.5",
  cell: "relative p-0 text-center text-sm h-9 w-9",
  day: "rdp-day h-9 w-9 p-0 font-normal text-sm text-white/80 rounded-lg hover:bg-white/10 transition-colors",
  day_selected: "rdp-day_selected bg-[#1F5EFF] text-white hover:bg-[#1F5EFF]",
  day_today: "rdp-day_today bg-white/10 text-white",
  day_outside: "rdp-day_outside text-white/20",
  day_disabled: "rdp-day_disabled text-white/20 hover:bg-transparent cursor-not-allowed",
  day_hidden: "invisible h-9 w-9",  // keep box size or the first week row collapses and dates shift off their weekdays
};

export function fmtDateLabel(iso) {
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-CA", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function DateField({ value, onChange, min, max, testid, className = "", placeholder = "Pick a date" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);

  const selected = value ? new Date(value + "T12:00:00") : undefined;
  const minD = min ? new Date(min + "T00:00:00") : undefined;
  const maxD = max ? new Date(max + "T23:59:59") : undefined;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`${className} flex items-center justify-between gap-2 text-left`}
      >
        <span className={value ? "" : "text-white/35"}>{value ? fmtDateLabel(value) : placeholder}</span>
        <CalendarDays className="w-4 h-4 text-white/40 flex-shrink-0" />
      </button>
      {open && (
        <div
          className="absolute z-50 mt-2 left-0 rounded-2xl bg-[#16161c] border border-white/12 shadow-2xl shadow-black/60 p-3"
          data-testid={`${testid}-popover`}
        >
          <DayPicker
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={(d) => { if (d) { onChange(toISODate(d)); setOpen(false); } }}
            disabled={(d) => (minD && d < minD) || (maxD && d > maxD)}
            classNames={CLASSES}
          />
        </div>
      )}
    </div>
  );
}
